/**
 * EL Editorial Image Grid
 * Reads server-rendered metaobject items, optionally shuffles them,
 * distributes into a masonry column layout, and applies scroll-reveal animations.
 *
 * Column count is determined by the actual rendered container width
 * (respects max-width and padding CSS), not by viewport width.
 * Minimum column width: 260px. Max columns: 7.
 */

if (typeof ElEditorialImageGrid === 'undefined') {
  window.ElEditorialImageGrid = class ElEditorialImageGrid {
    constructor(section) {
      this.section = section;
      this.inner = section.querySelector('.el-editorial-image-grid__inner');

      if (!this.inner) return;

      this.sourceContainer = this.inner.querySelector('.el-editorial-image-grid__items');
      this.gridContainer = this.inner.querySelector('.el-editorial-image-grid__grid');

      if (!this.sourceContainer || !this.gridContainer) return;

      this.randomize = this.inner.dataset.randomize === 'true';
      this.gapDesktop = parseInt(this.inner.dataset.gap, 10) || 20;
      this.gapMobile = parseInt(this.inner.dataset.gapMobile, 10) || this.gapDesktop;
      this.mobileBreakpoint = 768;
      this.minColumnWidth = 260;
      this.animate = this.inner.dataset.animate === 'true';
      this.visibleClass = 'el-is-visible';
      this.animateClass = 'el-editorial-image-grid__item--animate';
      this.resizeTimer = null;
      this.currentColumnCount = 0;
      this.orderedItems = [];
      this.revealQueue = [];
      this.isRevealing = false;
      this.revealTimer = null;
      this.resizeHandler = null;
      this.animationObserver = null;
      this.srcsetWidths = [320, 375, 400, 550, 700, 750, 900, 1050, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800];

      this.init();
    }

    destroy() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
      if (this.animationObserver) {
        this.animationObserver.disconnect();
        this.animationObserver = null;
      }
      if (this.revealTimer) {
        clearTimeout(this.revealTimer);
        this.revealTimer = null;
      }
      this.revealQueue = [];
      this.isRevealing = false;
    }

    init() {
      const items = Array.from(this.sourceContainer.querySelectorAll('.el-editorial-image-grid__item'));

      if (items.length === 0) return;

      const pinned = [];
      const unpinned = [];

      for (let i = 0; i < items.length; i++) {
        if (items[i].dataset.pinned === 'true') {
          pinned.push(items[i]);
        } else {
          unpinned.push(items[i]);
        }
      }

      if (this.randomize) {
        this.shuffle(unpinned);
      }

      this.orderedItems = pinned.concat(unpinned);

      this.wrapLinks();
      this.buildGrid();

      this.sourceContainer.style.display = 'none';

      if (this.animate) {
        this.initAnimations();
      }

      this.initResize();
    }

    /**
     * Returns the current gap value based on viewport width
     */
    getCurrentGap() {
      return window.innerWidth < this.mobileBreakpoint ? this.gapMobile : this.gapDesktop;
    }

    /**
     * Fisher-Yates shuffle (in-place)
     */
    shuffle(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = arr[i];
        arr[i] = arr[j];
        arr[j] = temp;
      }
    }

    /**
     * Wrap items that have a data-link attribute with an <a> tag
     */
    wrapLinks() {
      for (let i = 0; i < this.orderedItems.length; i++) {
        const item = this.orderedItems[i];
        const link = item.dataset.link;
        if (link) {
          const a = document.createElement('a');
          a.href = link;
          a.target = '_blank';
          a.rel = 'noopener';
          a.className = 'el-editorial-image-grid__link';
          item.appendChild(a);
        }
      }
    }

    /**
     * Determine column count based on the actual content width of the container.
     * clientWidth includes padding, so we subtract computed padding to get the
     * real available space. This correctly adapts to max-width + padding CSS.
     * Min column width: 260px, max columns: 7.
     */
    getColumnCount() {
      const style = getComputedStyle(this.inner);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = this.inner.clientWidth - paddingLeft - paddingRight;
      const gap = this.getCurrentGap();

      // cols * minWidth + (cols - 1) * gap <= availableWidth
      // cols <= (availableWidth + gap) / (minWidth + gap)
      const maxCols = Math.floor((availableWidth + gap) / (this.minColumnWidth + gap));

      return Math.max(1, Math.min(7, maxCols));
    }

    /**
     * Calculate actual column width in pixels based on real container dimensions.
     */
    getColumnWidth() {
      const style = getComputedStyle(this.inner);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = this.inner.clientWidth - paddingLeft - paddingRight;
      const gap = this.getCurrentGap();
      const cols = this.getColumnCount();
      return Math.floor((availableWidth - (cols - 1) * gap) / cols);
    }

    /**
     * Create an <img> element for an item from its data attributes.
     * The image is created with the correct sizes/srcset from the start,
     * so the browser always picks the optimal source — no race conditions.
     */
    createImage(item, sizesValue) {
      const baseSrc = item.dataset.imageSrc;
      const imgWidth = parseInt(item.dataset.imageWidth, 10);
      const imgHeight = parseInt(item.dataset.imageHeight, 10);
      const imgAlt = item.dataset.imageAlt || '';

      if (!baseSrc || !imgWidth) return null;

      // Build srcset with widths up to intrinsic width
      const validWidths = [];
      for (let k = 0; k < this.srcsetWidths.length; k++) {
        if (this.srcsetWidths[k] <= imgWidth) {
          validWidths.push(this.srcsetWidths[k]);
        }
      }
      if (validWidths.length === 0 || validWidths[validWidths.length - 1] < imgWidth) {
        validWidths.push(imgWidth);
      }

      const srcsetParts = [];
      for (let k = 0; k < validWidths.length; k++) {
        srcsetParts.push(baseSrc.replace(/width=\d+/, 'width=' + validWidths[k]) + ' ' + validWidths[k] + 'w');
      }

      // src must match the 1x column width to prevent browser loading max size
      const sizesNum = parseInt(sizesValue, 10) || 400;
      let srcWidth = validWidths[validWidths.length - 1]; // fallback to largest
      for (let k = 0; k < validWidths.length; k++) {
        if (validWidths[k] >= sizesNum) {
          srcWidth = validWidths[k];
          break;
        }
      }
      const src = baseSrc.replace(/width=\d+/, 'width=' + srcWidth);

      const img = document.createElement('img');
      img.setAttribute('width', imgWidth);
      img.setAttribute('height', imgHeight);
      img.setAttribute('loading', 'lazy');
      img.setAttribute('alt', imgAlt);
      img.className = 'el-editorial-image-grid__image';
      img.src = src;
      img.sizes = sizesValue;
      img.srcset = srcsetParts.join(', ');

      // Insert as first child (before overlay/link)
      item.insertBefore(img, item.firstChild);
      return img;
    }

    /**
     * Create images if they don't exist yet (first call), or update
     * sizes attribute on existing images (resize). Called before items
     * are distributed into columns.
     */
    updateImageSizes() {
      const colWidth = this.getColumnWidth();
      const sizesValue = colWidth + 'px';
      for (let i = 0; i < this.orderedItems.length; i++) {
        const item = this.orderedItems[i];
        const img = item.querySelector('img');
        if (!img) {
          this.createImage(item, sizesValue);
        } else {
          img.sizes = sizesValue;
        }
      }
    }

    /**
     * Build the masonry grid by creating columns and distributing items
     * into the shortest column first.
     */
    buildGrid() {
      const columnCount = this.getColumnCount();
      const gap = this.getCurrentGap();

      if (columnCount === this.currentColumnCount && this.gridContainer.children.length > 0) {
        return;
      }

      this.currentColumnCount = columnCount;
      this.gridContainer.innerHTML = '';
      this.gridContainer.style.gap = gap + 'px';

      const columns = [];
      const columnHeights = [];

      for (let c = 0; c < columnCount; c++) {
        const col = document.createElement('div');
        col.className = 'el-editorial-image-grid__column';
        col.style.display = 'flex';
        col.style.flexDirection = 'column';
        col.style.gap = gap + 'px';
        this.gridContainer.appendChild(col);
        columns.push(col);
        columnHeights.push(0);
      }

      // Set accurate sizes on images before they become visible
      this.updateImageSizes();

      for (let i = 0; i < this.orderedItems.length; i++) {
        const item = this.orderedItems[i];

        // Find the shortest column
        let shortestIndex = 0;
        let shortestHeight = columnHeights[0];
        for (let j = 1; j < columnHeights.length; j++) {
          if (columnHeights[j] < shortestHeight) {
            shortestHeight = columnHeights[j];
            shortestIndex = j;
          }
        }

        if (this.animate) {
          item.classList.add(this.animateClass);
        }

        columns[shortestIndex].appendChild(item);

        // Estimate height from data attributes or img element for masonry distribution
        const imgW = parseInt(item.dataset.imageWidth, 10);
        const imgH = parseInt(item.dataset.imageHeight, 10);
        let aspectRatio = 1;
        if (imgW && imgH) {
          aspectRatio = imgH / imgW;
        }
        columnHeights[shortestIndex] += aspectRatio * 100 + gap;
      }
    }

    /**
     * IntersectionObserver for scroll-reveal animation.
     * Items are queued and revealed one-by-one with a staggered delay (100ms)
     * for a cascading effect instead of all appearing at once.
     */
    initAnimations() {
      const items = this.gridContainer.querySelectorAll('.' + this.animateClass);

      if (items.length === 0) return;

      this.revealQueue = [];
      this.isRevealing = false;

      if ('IntersectionObserver' in window) {
        this.animationObserver = new IntersectionObserver(
          (entries) => {
            for (let i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                this.revealQueue.push(entries[i].target);
                this.animationObserver.unobserve(entries[i].target);
              }
            }
            if (!this.isRevealing) {
              this.processRevealQueue();
            }
          },
          {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px',
          }
        );

        for (let i = 0; i < items.length; i++) {
          this.animationObserver.observe(items[i]);
        }
      } else {
        for (let i = 0; i < items.length; i++) {
          items[i].classList.add(this.visibleClass);
        }
      }
    }

    /**
     * Process the reveal queue: reveal one item every 100ms
     */
    processRevealQueue() {
      if (this.revealQueue.length === 0) {
        this.isRevealing = false;
        return;
      }
      this.isRevealing = true;
      const item = this.revealQueue.shift();
      item.classList.add(this.visibleClass);
      this.revealTimer = setTimeout(() => this.processRevealQueue(), 100);
    }

    /**
     * Handle window resize: rebuild grid if column count changes
     */
    initResize() {
      this.resizeHandler = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          const newColumnCount = this.getColumnCount();
          if (newColumnCount !== this.currentColumnCount) {
            this.currentColumnCount = 0;
            this.buildGrid();
            if (this.animate) {
              this.initAnimations();
            }
          }
        }, 250);
      };
      window.addEventListener('resize', this.resizeHandler);
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll('.el-editorial-image-grid');
    for (let i = 0; i < sections.length; i++) {
      const instance = new ElEditorialImageGrid(sections[i]);
      sections[i]._elEditorialGrid = instance;
    }
  });

  // Re-initialize for Shopify Theme Editor
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('el-editorial-image-grid')) {
      if (event.target._elEditorialGrid) {
        event.target._elEditorialGrid.destroy();
      }
      const instance = new ElEditorialImageGrid(event.target);
      event.target._elEditorialGrid = instance;
    }
  });
}